import React, { useEffect, useId, useState } from 'react';
import { AccordionItem, Box, CoveragePeriods, ElectionAmount, EnrollmentStatus } from '@common-fe/common-fe';
import styled from 'styled-components';

import { PolicyStatus } from '@/modules/employee/EnrollmentDetails/queries/useGetPolicyStatuses.query';
import globalTheme from '@/styles/theme';

import { useExpandedItemStore } from '../../stores/useExpandedItem.store';

import EnrollmentExpandedPeriodCard from './EnrollmentExpandedPeriodCard';
import { EnrollmentPeriodCard } from './EnrollmentPeriodCard';

interface Props {
  coverageDateStart: string;
  coverageDateEnd: string;
  submitClaimsDateEnd: string;
  statusColor?: string;
  statuses?: PolicyStatus[];
  isDetailsMode?: boolean;
  enrollmentStatus?: EnrollmentStatus;
  coveragePeriods: CoveragePeriods[];
  employeeName?: string;
  externalDeductibleMet?: string;
  isRollover?: boolean;
  electionPeriods?: ElectionAmount[];
}

const MIN_HEIGHT_ENROLLMENT_ITEM = '104px';
const MIN_HEIGHT_ENROLLMENT_ITEM_WITH_ROLLOVER_BLOCK = '168px';

const AccordionItemWrapper = styled(Box)`
  a {
    display: block;
    color: ${globalTheme.colors.textSecondary};
  }
`;

export const EnrollmentDates: React.FC<Props> = ({
  coverageDateStart,
  coverageDateEnd,
  submitClaimsDateEnd,
  statusColor,
  statuses = [],
  isDetailsMode,
  enrollmentStatus,
  coveragePeriods = [],
  employeeName,
  externalDeductibleMet,
  isRollover,
  electionPeriods = [],
}) => {
  const [expanded, setExpanded] = useState(false);
  const id = useId();

  const expandedItemId = useExpandedItemStore((state) => state.expandedItemId);
  const setCurrentExpandedItemId = useExpandedItemStore((state) => state.setExpandedItemId);

  useEffect(() => {
    if (expandedItemId !== id) {
      setExpanded(false);
    }
  }, [expandedItemId, expanded, id]);

  if (coveragePeriods.length > 1 || statuses.length > 1 || electionPeriods.length > 1) {
    return (
      <Box
        style={{
          position: 'relative',
          cursor: 'pointer',
        }}
        margin={{ top: isDetailsMode ? 'm' : '0' }}
        height={{
          min: !isDetailsMode
            ? isRollover
              ? MIN_HEIGHT_ENROLLMENT_ITEM_WITH_ROLLOVER_BLOCK
              : MIN_HEIGHT_ENROLLMENT_ITEM
            : 'auto',
        }}
      >
        <AccordionItemWrapper
          width="100%"
          background={!isDetailsMode ? 'canvas' : ''}
          round="container1Round"
          border={!isDetailsMode && { color: 'border2', size: 'small' }}
          pad={{ vertical: !isDetailsMode ? 'spacing16' : '0', horizontal: !isDetailsMode ? 'spacing24' : '0' }}
          style={{
            position: isDetailsMode ? 'relative' : 'absolute',
            zIndex: expanded ? 111 : 110,
          }}
          onClick={() => {
            setExpanded(!expanded);
            setCurrentExpandedItemId(id);
          }}
        >
          <AccordionItem
            isExpanded={expanded}
            iconSize="20px"
            text={
              <EnrollmentPeriodCard
                coverageDateStart={coverageDateStart}
                coverageDateEnd={coverageDateEnd}
                submitClaimsDateEnd={submitClaimsDateEnd}
                statusColor={statusColor}
                enrollmentStatus={enrollmentStatus}
                coveragePeriods={coveragePeriods}
                isExpanded={expanded}
                isDetailsMode={isDetailsMode}
                isRollover={isRollover}
              />
            }
            isLeftArrow
          >
            <EnrollmentExpandedPeriodCard
              statusColor={statusColor}
              isDetailsMode={isDetailsMode}
              statuses={statuses}
              enrollmentStatus={enrollmentStatus}
              coveragePeriods={coveragePeriods}
              electionPeriods={electionPeriods}
            />
          </AccordionItem>
        </AccordionItemWrapper>
      </Box>
    );
  }
  return (
    <Box
      background={!isDetailsMode ? 'canvas' : ''}
      round="container1Round"
      border={!isDetailsMode && { color: 'border2', size: 'small' }}
      pad={{ vertical: !isDetailsMode ? 'spacing16' : '0', horizontal: !isDetailsMode ? 'spacing24' : '0' }}
      margin={{ top: isDetailsMode ? 'm' : '0' }}
    >
      <EnrollmentPeriodCard
        coverageDateStart={coverageDateStart}
        coverageDateEnd={coverageDateEnd}
        submitClaimsDateEnd={submitClaimsDateEnd}
        statusColor={statusColor}
        enrollmentStatus={enrollmentStatus}
        coveragePeriods={coveragePeriods}
        isDetailsMode={isDetailsMode}
        employeeName={employeeName}
        externalDeductibleMet={externalDeductibleMet}
        isRollover={isRollover}
      />
    </Box>
  );
};
